/**
 * useSpotPriceSubscriptions Hook
 *
 * Manages CCXT Pro WebSocket subscriptions for spot exchanges.
 * Uses real-time WebSocket streams for instant price updates.
 *
 * Exchanges:
 * - Binance Spot: watchTickers() WebSocket (USDT pairs)
 * - Upbit: watchTickers() WebSocket (KRW pairs)
 * - Bithumb: watchTickers() WebSocket (KRW pairs)
 *
 * Note: Using CCXT Pro (now free) for real-time WebSocket updates.
 * Provides instant price updates as they happen on the exchange.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import type { Exchange as CcxtExchange } from 'ccxt';
import { Exchange } from '../types/futuresSpot';

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
type CcxtModuleWithPro = CcxtModule & { pro?: Record<string, unknown> };
let ccxtModulePromise: Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> | null = null;

async function loadCcxtModule(): Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> {
  console.log('[useSpotPriceSubscriptions] loadCcxtModule called');
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      console.log('[useSpotPriceSubscriptions] CCXT import successful!', { hasDefault: !!raw.default, keys: Object.keys(raw).slice(0, 5) });
      const resolved = (raw.default ?? raw) as CcxtModuleWithPro;
      const hasProNamespace = resolved.pro && typeof resolved.pro === 'object';
      console.log('[useSpotPriceSubscriptions] CCXT Pro namespace check:', { hasProNamespace, proKeys: hasProNamespace ? Object.keys(resolved.pro!).slice(0, 5) : [] });
      return {
        module: resolved,
        usingPro: Boolean(hasProNamespace),
      };
    }).catch((error) => {
      console.error('[useSpotPriceSubscriptions] CCXT import FAILED:', error);
      throw error;
    });
  }
  return ccxtModulePromise;
}

/**
 * Spot price update callback
 */
export type SpotPriceCallback = (symbol: string, exchange: Exchange, price: number) => void;

/**
 * Connection status callback
 */
export type ConnectionStatusCallback = (exchange: Exchange, status: 'connected' | 'disconnected' | 'reconnecting') => void;

/**
 * Error callback
 */
export type ErrorCallback = (exchange: Exchange, error: string) => void;

/**
 * Hook configuration
 */
interface UseSpotPriceSubscriptionsConfig {
  /** Symbols to subscribe to (deprecated - use symbolsByExchange) */
  symbols?: string[];

  /** Exchange-specific symbols */
  symbolsByExchange?: {
    binance?: string[];
    upbit?: string[];
    bithumb?: string[];
  };

  /** Enable subscriptions (default: true) */
  enabled?: boolean;

  /** Callback for price updates */
  onPriceUpdate: SpotPriceCallback;

  /** Callback for connection status changes */
  onConnectionStatus?: ConnectionStatusCallback;

  /** Callback for errors */
  onError?: ErrorCallback;

  /** Enable debug logging (default: false) */
  enableLogging?: boolean;
}

/**
 * WebSocket reconnection configuration
 */
const INITIAL_RETRY_DELAY_MS = 1000; // Start with 1 second
const MAX_RETRY_DELAY_MS = 60000; // Max 60 seconds
const BACKOFF_MULTIPLIER = 2; // Double delay each retry

/**
 * Custom hook for spot exchange price subscriptions
 *
 * @param config - Hook configuration
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   useSpotPriceSubscriptions({
 *     symbols: ['BTC/USDT', 'ETH/USDT'],
 *     onPriceUpdate: (symbol, exchange, price) => {
 *       console.log(`${symbol} on ${exchange}: ${price}`);
 *     },
 *   });
 * }
 * ```
 */
export function useSpotPriceSubscriptions(config: UseSpotPriceSubscriptionsConfig): void {
  const {
    symbols = [],
    symbolsByExchange,
    enabled = true,
    onPriceUpdate,
    onConnectionStatus,
    onError,
    enableLogging = false,
  } = config;

  // Exchange client refs (stable across renders)
  const binanceSpotRef = useRef<CcxtExchange | null>(null);
  const upbitRef = useRef<CcxtExchange | null>(null);
  const bithumbRef = useRef<CcxtExchange | null>(null);

  // CCXT module loaded state
  const [ccxtLoaded, setCcxtLoaded] = useState(false);

  // Active WebSocket connection flags
  const connectionsRef = useRef({
    binanceSpot: false,
    upbit: false,
    bithumb: false,
  });

  // Track whether we've already called 'connected' status (to prevent re-setting to 'reconnecting')
  const connectedStatusCalledRef = useRef({
    binanceSpot: false,
    upbit: false,
    bithumb: false,
  });

  /**
   * Subscribe to Binance Spot tickers using WebSocket
   */
  const subscribeBinanceSpot = useCallback(
    async (usdtSymbols: string[]) => {
      if (!binanceSpotRef.current || usdtSymbols.length === 0 || !enabled) return;

      // Note: connectionsRef.current.binanceSpot is set BEFORE calling this function to prevent double-mount

      if (enableLogging) {
        console.info(
          `[useSpotPriceSubscriptions] Starting Binance Spot WebSocket: ${usdtSymbols.length} symbols`
        );
      }

      try {
        // Track connection state to only call status callback once per state change
        let hasCalledConnected = false;

        // Mark as reconnecting ONLY if we haven't already set 'connected' (prevents React Strict Mode double-mount)
        if (!connectedStatusCalledRef.current.binanceSpot) {
          onConnectionStatus?.(Exchange.Binance, 'reconnecting');
        }

        while (enabled && connectionsRef.current.binanceSpot && binanceSpotRef.current) {
          const tickers = await binanceSpotRef.current.watchTickers(usdtSymbols);

          // Mark as connected on first successful data receipt only
          if (!hasCalledConnected) {
            onConnectionStatus?.(Exchange.Binance, 'connected');
            connectedStatusCalledRef.current.binanceSpot = true; // Track that we've called 'connected'
            hasCalledConnected = true;
          }

          // Process all tickers
          for (const symbol in tickers) {
            const ticker = tickers[symbol];
            if (ticker && ticker.last != null) {
              onPriceUpdate(symbol, Exchange.Binance, ticker.last);

              if (enableLogging && Math.random() < 0.001) {
                // Log 0.1% of updates for debugging
                console.debug('[useSpotPriceSubscriptions] Binance price update', {
                  symbol,
                  price: ticker.last,
                });
              }
            }
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useSpotPriceSubscriptions] Binance Spot WebSocket error:', errorMessage);
        onError?.(Exchange.Binance, errorMessage);
        onConnectionStatus?.(Exchange.Binance, 'disconnected');
        // Reset the connected status flag so we can call 'connected' again after reconnection
        connectedStatusCalledRef.current.binanceSpot = false;
      }
    },
    [enabled, onPriceUpdate, onConnectionStatus, onError, enableLogging]
  );

  /**
   * Subscribe to Upbit tickers using WebSocket
   */
  const subscribeUpbit = useCallback(
    async (krwSymbols: string[]) => {
      if (!upbitRef.current || krwSymbols.length === 0 || !enabled) return;

      // Note: connectionsRef.current.upbit is set BEFORE calling this function to prevent double-mount

      if (enableLogging) {
        console.info(
          `[useSpotPriceSubscriptions] Starting Upbit WebSocket: ${krwSymbols.length} symbols`
        );
      }

      try {
        // Track connection state to only call status callback once per state change
        let hasCalledConnected = false;

        // Mark as reconnecting ONLY if we haven't already set 'connected' (prevents React Strict Mode double-mount)
        if (!connectedStatusCalledRef.current.upbit) {
          onConnectionStatus?.(Exchange.Upbit, 'reconnecting');
        }

        while (enabled && connectionsRef.current.upbit && upbitRef.current) {
          const tickers = await upbitRef.current.watchTickers(krwSymbols);

          // Mark as connected on first successful data receipt only
          if (!hasCalledConnected) {
            onConnectionStatus?.(Exchange.Upbit, 'connected');
            connectedStatusCalledRef.current.upbit = true;
            hasCalledConnected = true;
          }

          // Process all tickers
          for (const symbol in tickers) {
            const ticker = tickers[symbol];
            if (ticker && ticker.last != null) {
              onPriceUpdate(symbol, Exchange.Upbit, ticker.last);

              if (enableLogging && Math.random() < 0.001) {
                // Log 0.1% of updates for debugging
                console.debug('[useSpotPriceSubscriptions] Upbit price update', {
                  symbol,
                  price: ticker.last,
                });
              }
            }
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useSpotPriceSubscriptions] Upbit WebSocket error:', errorMessage);
        onError?.(Exchange.Upbit, errorMessage);
        onConnectionStatus?.(Exchange.Upbit, 'disconnected');
        // Reset the connected status flag so we can call 'connected' again after reconnection
        connectedStatusCalledRef.current.upbit = false;
      }
    },
    [enabled, onPriceUpdate, onConnectionStatus, onError, enableLogging]
  );

  /**
   * Subscribe to Bithumb tickers using WebSocket
   */
  const subscribeBithumb = useCallback(
    async (krwSymbols: string[]) => {
      if (!bithumbRef.current || krwSymbols.length === 0 || !enabled) return;

      // Note: connectionsRef.current.bithumb is set BEFORE calling this function to prevent double-mount

      if (enableLogging) {
        console.info(
          `[useSpotPriceSubscriptions] Starting Bithumb WebSocket: ${krwSymbols.length} symbols`
        );
      }

      try {
        // Track connection state to only call status callback once per state change
        let hasCalledConnected = false;

        // Mark as reconnecting ONLY if we haven't already set 'connected' (prevents React Strict Mode double-mount)
        if (!connectedStatusCalledRef.current.bithumb) {
          onConnectionStatus?.(Exchange.Bithumb, 'reconnecting');
        }

        while (enabled && connectionsRef.current.bithumb && bithumbRef.current) {
          const tickers = await bithumbRef.current.watchTickers(krwSymbols);

          // Mark as connected on first successful data receipt only
          if (!hasCalledConnected) {
            onConnectionStatus?.(Exchange.Bithumb, 'connected');
            connectedStatusCalledRef.current.bithumb = true; // Track that we've called 'connected'
            hasCalledConnected = true;
          }

          // Process all tickers
          for (const symbol in tickers) {
            const ticker = tickers[symbol];
            if (ticker && ticker.last != null) {
              onPriceUpdate(symbol, Exchange.Bithumb, ticker.last);

              if (enableLogging && Math.random() < 0.001) {
                // Log 0.1% of updates for debugging
                console.debug('[useSpotPriceSubscriptions] Bithumb price update', {
                  symbol,
                  price: ticker.last,
                });
              }
            }
          }
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useSpotPriceSubscriptions] Bithumb WebSocket error:', errorMessage);
        onError?.(Exchange.Bithumb, errorMessage);
        onConnectionStatus?.(Exchange.Bithumb, 'disconnected');
        // Reset the connected status flag so we can call 'connected' again after reconnection
        connectedStatusCalledRef.current.bithumb = false;
      }
    },
    [enabled, onPriceUpdate, onConnectionStatus, onError, enableLogging]
  );

  /**
   * Load CCXT module and initialize exchange clients on mount
   */
  useEffect(() => {
    if (!enabled) return;

    // Skip if already initialized (prevent React Strict Mode double-initialization)
    if (ccxtLoaded && binanceSpotRef.current && upbitRef.current && bithumbRef.current) {
      console.log('[useSpotPriceSubscriptions] CCXT clients already initialized, skipping');
      return;
    }

    let mounted = true;

    // Load CCXT dynamically
    loadCcxtModule().then(({ module: ccxtModule, usingPro }) => {
      if (!mounted) return;

      if (!usingPro) {
        console.warn('[useSpotPriceSubscriptions] CCXT Pro namespace not available - WebSocket subscriptions will not work. Using regular CCXT.');
        onError?.(Exchange.Binance, 'CCXT Pro not available - WebSocket subscriptions disabled');
        return;
      }

      console.info('[useSpotPriceSubscriptions] Initializing spot exchange clients (CCXT Pro WebSocket)...');

      // Type assertion for pro namespace access
      type ExchangeConstructor = new (params?: Record<string, unknown>) => CcxtExchange;
      const proBinance = (ccxtModule.pro as Record<string, ExchangeConstructor>)['binance'];
      const proUpbit = (ccxtModule.pro as Record<string, ExchangeConstructor>)['upbit'];
      const proBithumb = (ccxtModule.pro as Record<string, ExchangeConstructor>)['bithumb'];

      // Initialize CCXT Pro clients for WebSocket access
      // NOTE: Upbit binary text decoding is handled by polyfill in main.tsx
      binanceSpotRef.current = new proBinance({
        enableRateLimit: true,
        options: { defaultType: 'spot' },
      });

      upbitRef.current = new proUpbit({
        enableRateLimit: true,
      });

      bithumbRef.current = new proBithumb({
        enableRateLimit: true,
      });

      console.info('[useSpotPriceSubscriptions] Spot exchange clients initialized');
      setCcxtLoaded(true);
    }).catch((error) => {
      console.error('[useSpotPriceSubscriptions] Failed to load CCXT module:', error);
      onError?.(Exchange.Binance, `Failed to load CCXT: ${error.message}`);
    });

    // Cleanup on unmount - but DON'T reset ccxtLoaded in React Strict Mode
    return () => {
      mounted = false;
      console.info('[useSpotPriceSubscriptions] Cleanup function called (React Strict Mode or actual unmount)');

      // Only stop connections, but keep clients initialized
      // This prevents React Strict Mode from breaking the initialization
      connectionsRef.current.binanceSpot = false;
      connectionsRef.current.upbit = false;
      connectionsRef.current.bithumb = false;
    };
  }, [enabled, onError, ccxtLoaded]);

  /**
   * Start WebSocket subscriptions when symbols change (wait for CCXT to load first)
   */
  useEffect(() => {
    if (!enabled || !ccxtLoaded) return;

    // Use exchange-specific symbols if provided, otherwise fall back to legacy behavior
    let binanceSymbols: string[] = [];
    let upbitSymbols: string[] = [];
    let bithumbSymbols: string[] = [];

    if (symbolsByExchange) {
      // NEW: Use exchange-specific symbol lists
      binanceSymbols = symbolsByExchange.binance || [];
      upbitSymbols = symbolsByExchange.upbit || [];
      bithumbSymbols = symbolsByExchange.bithumb || [];
    } else {
      // LEGACY: Separate symbols by quote currency (deprecated)
      symbols.forEach((symbol) => {
        if (symbol.includes('USDT')) {
          binanceSymbols.push(symbol);
        } else if (symbol.includes('KRW')) {
          upbitSymbols.push(symbol);
          bithumbSymbols.push(symbol);
        }
      });
    }

    // Check if symbols actually changed to prevent infinite loop
    const binanceKey = binanceSymbols.sort().join(',');
    const upbitKey = upbitSymbols.sort().join(',');
    const bithumbKey = bithumbSymbols.sort().join(',');

    if (
      connectionsRef.current.binanceSpot &&
      connectionsRef.current.upbit &&
      connectionsRef.current.bithumb
    ) {
      // Already running - no need to restart
      return;
    }

    console.info(
      `[useSpotPriceSubscriptions] Starting WebSocket subscriptions:`,
      {
        binance: binanceSymbols.length,
        upbit: upbitSymbols.length,
        bithumb: bithumbSymbols.length,
        binanceFirst5: binanceSymbols.slice(0, 5),
        upbitFirst5: upbitSymbols.slice(0, 5),
        bithumbFirst5: bithumbSymbols.slice(0, 5),
        usingSymbolsByExchange: !!symbolsByExchange,
      }
    );

    // Start WebSocket subscriptions for each exchange with their specific symbols
    // CRITICAL: Set connection flag BEFORE calling subscribe to prevent React Strict Mode double-mount
    // These run in background loops - don't await
    if (binanceSymbols.length > 0 && !connectionsRef.current.binanceSpot) {
      console.info(`[useSpotPriceSubscriptions] ✅ Starting Binance Spot with ${binanceSymbols.length} symbols`);
      connectionsRef.current.binanceSpot = true; // Set BEFORE calling subscribe
      subscribeBinanceSpot(binanceSymbols);
    } else if (connectionsRef.current.binanceSpot) {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Binance Spot: ALREADY RUNNING - skipping duplicate subscription');
    } else {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Binance Spot: NO SYMBOLS - skipping subscription');
    }

    if (upbitSymbols.length > 0 && !connectionsRef.current.upbit) {
      console.info(`[useSpotPriceSubscriptions] ✅ Starting Upbit with ${upbitSymbols.length} symbols`);
      connectionsRef.current.upbit = true; // Set BEFORE calling subscribe
      subscribeUpbit(upbitSymbols);
    } else if (connectionsRef.current.upbit) {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Upbit: ALREADY RUNNING - skipping duplicate subscription');
    } else {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Upbit: NO SYMBOLS - skipping subscription');
    }

    if (bithumbSymbols.length > 0 && !connectionsRef.current.bithumb) {
      console.info(`[useSpotPriceSubscriptions] ✅ Starting Bithumb with ${bithumbSymbols.length} symbols`);
      connectionsRef.current.bithumb = true; // Set BEFORE calling subscribe
      subscribeBithumb(bithumbSymbols);
    } else if (connectionsRef.current.bithumb) {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Bithumb: ALREADY RUNNING - skipping duplicate subscription');
    } else {
      console.warn('[useSpotPriceSubscriptions] ⚠️ Bithumb: NO SYMBOLS - skipping subscription');
    }

    // Cleanup function to stop WebSocket connections
    return () => {
      // Signal all connections to stop
      connectionsRef.current.binanceSpot = false;
      connectionsRef.current.upbit = false;
      connectionsRef.current.bithumb = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ccxtLoaded]);
}
